import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { setAllWithdrawals } from '../../store/adminSlice';
import { fmtUSD } from '../../utils/formatters';
import { adminApi } from '../../services/api';

const DepositsWithdrawals = ({ type }) => {
  const dispatch = useDispatch();
  const { allDeposits, allWithdrawals } = useSelector((state) => state.admin);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingIds, setProcessingIds] = useState([]);
  const [deletingIds, setDeletingIds] = useState([]);

  const data = type === 'deposits' ? allDeposits : allWithdrawals;
  const isDeposit = type === 'deposits';

  const filteredData = data.filter(item => {
    const search = searchTerm.toLowerCase();
    const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim().toLowerCase();
    const matchesSearch = item.userEmail?.toLowerCase().includes(search) ||
                          fullName.includes(search) ||
                          item.method?.toLowerCase().includes(search);
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalAmount = filteredData.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const pendingCount = filteredData.filter(item => item.status === 'pending').length;
  const completedCount = filteredData.filter(item => item.status === 'completed').length;

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]">COMPLETED</span>;
    }
    if (status === 'pending') {
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b45309]">PENDING</span>;
    }
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b91c1c]">REJECTED</span>;
  };

  const handleApprove = async (id) => {
    setProcessingIds(prev => [...prev, id]);
    try {
      await adminApi.post(`/admin/withdrawals/${id}/approve`);
      const updated = allWithdrawals.map(w => w.id === id ? { ...w, status: 'completed' } : w);
      dispatch(setAllWithdrawals(updated));
      dispatch(showToast({ message: 'Withdrawal approved successfully', type: 'success' }));
    } catch (error) { dispatch(showToast({ message: 'Failed to approve withdrawal', type: 'error' })); }
    finally { setProcessingIds(prev => prev.filter(pid => pid !== id)); }
  };

  const handleReject = async (id) => {
    setProcessingIds(prev => [...prev, id]);
    try {
      await adminApi.post(`/admin/withdrawals/${id}/reject`, { reason: 'Rejected by admin' });
      const updated = allWithdrawals.map(w => w.id === id ? { ...w, status: 'rejected' } : w);
      dispatch(setAllWithdrawals(updated));
      dispatch(showToast({ message: 'Withdrawal rejected', type: 'error' }));
    } catch (error) { dispatch(showToast({ message: 'Failed to reject withdrawal', type: 'error' })); }
    finally { setProcessingIds(prev => prev.filter(pid => pid !== id)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this withdrawal? This cannot be undone. If completed, the amount will be refunded to the user.')) return;
    setDeletingIds(prev => [...prev, id]);
    try {
      await adminApi.delete(`/admin/withdrawals/${id}`);
      const updated = allWithdrawals.filter(w => w.id !== id);
      dispatch(setAllWithdrawals(updated));
      dispatch(showToast({ message: 'Withdrawal deleted successfully', type: 'success' }));
    } catch (error) {
      dispatch(showToast({ message: error.response?.data?.message || 'Failed to delete withdrawal', type: 'error' }));
    }
    finally { setDeletingIds(prev => prev.filter(did => did !== id)); }
  };

  const getInitials = (item) => {
    if (item.firstName && item.lastName) return (item.firstName[0] + item.lastName[0]).toUpperCase();
    return (item.userEmail?.[0] || 'U').toUpperCase();
  };

  const getDisplayName = (item) => {
    if (item.firstName && item.lastName) return `${item.firstName} ${item.lastName}`;
    return item.userEmail || 'Unknown';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            {isDeposit ? 'DEPOSITS' : 'WITHDRAWALS'}
          </h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">
            View all {isDeposit ? 'deposit' : 'withdrawal'} transactions
          </p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8] text-xs" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email or method..."
              className="pl-10 pr-4 py-2.5 bg-white border-2 border-[#cbd5e1] rounded-xl text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all w-full sm:w-64 placeholder:text-[#94a3b8]" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border-2 border-[#cbd5e1] rounded-xl text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all cursor-pointer">
            <option value="all">ALL STATUS</option>
            <option value="completed">COMPLETED</option>
            <option value="pending">PENDING</option>
            <option value="rejected">REJECTED</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 p-5 border-b-2 border-[#e2e8f0] bg-white">
        {[
          { label: `TOTAL ${isDeposit ? 'DEPOSITS' : 'WITHDRAWALS'}`, value: fmtUSD(totalAmount), color: '#1e3a5f', icon: isDeposit ? 'fa-circle-plus' : 'fa-circle-minus' },
          { label: 'COMPLETED', value: completedCount, color: '#059669', icon: 'fa-check-circle' },
          { label: 'PENDING', value: pendingCount, color: '#d97706', icon: 'fa-clock' },
        ].map(stat => (
          <div key={stat.label} className="flex items-center gap-3 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${stat.color}15` }}>
              <i className={`fas ${stat.icon} text-sm`} style={{ color: stat.color }} />
            </div>
            <div>
              <div className="text-[8px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider">{stat.label}</div>
              <div className="text-lg font-extrabold mt-0.5" style={{ color: stat.color }}>{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0]">
              {['DATE', 'USER', 'METHOD', 'AMOUNT', 'STATUS', 'REFERENCE', ...(!isDeposit ? ['ACTIONS'] : [])].map(h => (
                <th key={h} className={`px-5 py-3.5 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider ${h === 'AMOUNT' ? 'text-right' : h === 'ACTIONS' ? 'text-center' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={isDeposit ? 6 : 7} className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
                    <i className={`fas ${isDeposit ? 'fa-circle-plus' : 'fa-circle-minus'} text-[#94a3b8] text-2xl`} />
                  </div>
                  <p className="text-[#475569] font-syne font-extrabold text-sm uppercase tracking-wider">
                    No {isDeposit ? 'deposits' : 'withdrawals'} found
                  </p>
                </td>
              </tr>
            ) : (
              filteredData.map((item, idx) => (
                <tr key={item.id || idx} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all">
                  {/* Date */}
                  <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">
                    {item.time ? new Date(item.time).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  {/* User */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center flex-shrink-0 shadow-sm">
                        <span className="text-[9px] font-extrabold text-white">{getInitials(item)}</span>
                      </div>
                      <div>
                        <div className="text-[12px] text-[#0f172a] font-extrabold truncate max-w-[150px]">{getDisplayName(item)}</div>
                        {item.firstName && item.lastName && (
                          <div className="text-[9px] text-[#64748b] font-mono font-bold truncate max-w-[150px]">{item.userEmail}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  {/* Method */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <i className={`fas ${item.method === 'card' ? 'fa-credit-card' : item.method === 'bank' ? 'fa-building-columns' : item.method === 'crypto' ? 'fa-coins' : 'fa-wallet'} text-[#1e3a5f] text-xs`} />
                      <span className="text-[12px] text-[#475569] font-bold capitalize">{item.method}</span>
                    </div>
                  </td>
                  {/* Amount */}
                  <td className={`px-5 py-3.5 text-right font-mono text-[12px] font-extrabold ${isDeposit ? 'text-[#059669]' : 'text-[#dc2626]'}`}>
                    {isDeposit ? '+' : '-'}{fmtUSD(item.amount)}
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3.5">{getStatusBadge(item.status)}</td>
                  {/* Reference */}
                  <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">{item.reference || item.transactionId || '—'}</td>
                  {/* Actions */}
                  {!isDeposit && (
                    <td className="px-5 py-3.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {item.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(item.id)} disabled={processingIds.includes(item.id)}
                              className="px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all disabled:opacity-50 border-b-[2px] border-[#047857]">
                              {processingIds.includes(item.id) ? <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><i className="fas fa-check mr-1" /> APPROVE</>}
                            </button>
                            <button onClick={() => handleReject(item.id)} disabled={processingIds.includes(item.id)}
                              className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[2px] border-[#b91c1c]">
                              {processingIds.includes(item.id) ? <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><i className="fas fa-times mr-1" /> REJECT</>}
                            </button>
                          </>
                        )}
                        <button onClick={() => handleDelete(item.id)} disabled={deletingIds.includes(item.id)}
                          className="px-3 py-1.5 rounded-lg bg-[#64748b] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#475569] transition-all disabled:opacity-50 border-b-[2px] border-[#475569]">
                          {deletingIds.includes(item.id) ? <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="fas fa-trash" />}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DepositsWithdrawals;